import React, { useState } from 'react';
import styled from 'styled-components';
import { ChevronDown, ChevronRight, Sigma } from 'lucide-react';
import { INK } from '../onboarding/review/reviewStyles';
import { Note } from '../onboarding/review/contractReviewStyles';
import type {
  AmendmentGroup, AmendmentSimulation, BookItem, DiffItem, FieldChange, PendingTermDecision, PeriodVerdict, TermItem, WorkingBlock,
} from '../types/amendmentReview';
import { Button, fieldBase } from '../onboarding/review/reviewStyles';
import { parseAmountShorthand, replaceShorthandOnBlur } from '../../../utils/amountUtils';
import { CHANGE_COLOUR, CHANGE_LABEL, fieldLabel, formatDate, formatNumber, formatValue, signed } from './amendmentPresentation';

/* ------------------------------------------------------------------ styling (review standard) */

export const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 13px; margin: 8px 0 14px;
  th, td { padding: 8px 10px; text-align: left; border-bottom: 1px solid ${INK.line}; vertical-align: top; }
  th { color: ${INK.muted}; font-size: 12px; font-weight: 650; letter-spacing: .04em; text-transform: uppercase; }
  td { color: ${INK.body}; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  td.muted { color: ${INK.muted}; }
  td.source { color: ${INK.faint}; font-size: 12px; max-width: 28ch; overflow-wrap: anywhere; }
  tr[data-carried='true'] td { color: ${INK.muted}; }
  @media (max-width: 700px) { display: block; overflow-x: auto; }
`;

export const Chip = styled.span<{ $colour: string }>`
  display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 999px;
  border: 1px solid ${p => p.$colour}55; color: ${p => p.$colour}; background: ${p => p.$colour}14;
  font-size: 11px; font-weight: 700; letter-spacing: .04em; white-space: nowrap;
`;

const Working = styled.details`
  margin: 10px 0 16px; border: 1px solid ${INK.line}; border-radius: 9px; background: ${INK.raised};
  > summary { cursor: pointer; padding: 10px 14px; color: ${INK.accentSoft}; font-size: 13px; font-weight: 650; display: flex; align-items: center; gap: 8px; list-style: none; }
  > summary::-webkit-details-marker { display: none; }
  > div { padding: 0 14px 12px; }
  .block + .block { margin-top: 12px; padding-top: 12px; border-top: 1px solid ${INK.line}; }
  .formula { font-family: ui-monospace, 'JetBrains Mono', monospace; font-size: 12px; color: ${INK.strong}; margin: 4px 0 6px; }
  .label { color: ${INK.muted}; font-size: 12px; font-weight: 650; }
  .result { margin-top: 6px; color: ${INK.strong}; font-weight: 700; font-variant-numeric: tabular-nums; }
`;

const Before = styled.span`color: ${INK.muted}; text-decoration: line-through; text-decoration-color: ${INK.faint};`;
const After = styled.strong`color: ${INK.strong};`;
const Arrow = styled.span`color: ${INK.faint}; margin: 0 6px;`;

export const Pair: React.FC<{ before: unknown; after: unknown; field?: string }> = ({ before, after, field }) => (
  <span>
    <Before>{formatValue(before, field)}</Before><Arrow>→</Arrow><After>{formatValue(after, field)}</After>
  </span>
);

/* ------------------------------------------------------------------ working */

export const WorkingPanel: React.FC<{ blocks?: WorkingBlock[]; title?: string; open?: boolean }> = ({ blocks, title, open }) => {
  const [expanded, setExpanded] = useState(Boolean(open));
  if (!blocks || blocks.length === 0) return null;
  return <Working open={expanded} onToggle={event => setExpanded(event.currentTarget.open)}>
    <summary>{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}<Sigma size={14} /> {title || 'How this was computed'} <span style={{ color: INK.faint, fontWeight: 400 }}>· {blocks.length} figure{blocks.length === 1 ? '' : 's'} with their working</span></summary>
    <div>
      {blocks.map((block, index) => <div className="block" key={index}>
        <div className="label">{block.label}</div>
        <div className="formula">{block.formula}</div>
        <Table>
          <tbody>
            {block.inputs.map((input, i) => <tr key={i}>
              <td className="muted" style={{ width: '28%' }}>{input.name}</td>
              <td className="num" style={{ width: '22%' }}>{formatValue(input.value, input.name)}</td>
              <td className="source">{input.source}</td>
            </tr>)}
          </tbody>
        </Table>
        <div className="result">= {formatValue(block.result, block.label)}</div>
      </div>)}
    </div>
  </Working>;
};

/* ------------------------------------------------------------------ diff table */

const DiffTable: React.FC<{ items: DiffItem[]; emptyText: string; showRun?: boolean; showArmed?: boolean }> = ({ items, emptyText, showRun, showArmed }) => {
  if (!items.length) return <p style={{ color: INK.muted }}>{emptyText}</p>;
  return <Table>
    <thead><tr><th>Item</th><th>Change</th><th>Before → after</th>{showRun && <th>Latest test</th>}{showArmed && <th>In force</th>}<th>Source</th></tr></thead>
    <tbody>
      {items.map(item => <tr key={item.key} data-carried={item.change === 'CARRIED'}>
        <td><strong>{item.label}</strong>{item.carriedFromVersionNo != null && <div style={{ color: INK.faint, fontSize: 12 }}>carried from version {item.carriedFromVersionNo}</div>}</td>
        <td><Chip $colour={CHANGE_COLOUR[item.change]}>{CHANGE_LABEL[item.change]}</Chip></td>
        <td>
          {item.change === 'CARRIED' && <span style={{ color: INK.muted }}>unchanged</span>}
          {item.change === 'ADDED' && <span>{summariseRow(item.afterRow)}</span>}
          {item.change === 'REMOVED' && <Before>{summariseRow(item.beforeRow)}</Before>}
          {item.change === 'CHANGED' && item.fieldChanges.map(change => <div key={change.field}>
            <span style={{ color: INK.muted, fontSize: 12 }}>{fieldLabel(change.field)}: </span><Pair before={change.before} after={change.after} field={change.field} />
          </div>)}
        </td>
        {showRun && <td>{item.latestRun
          ? <span>{formatValue(item.latestRun.actualValue, 'value')} on {formatDate(item.latestRun.testDate)} · {passLabel(item.latestRun.passesBefore)} before, {passLabel(item.latestRun.passesAfter)} after</span>
          : <span style={{ color: INK.faint }}>no run yet</span>}</td>}
        {showArmed && <td>{item.armed ? <Chip $colour="#edcc82">Armed now</Chip> : <span style={{ color: INK.faint }}>not armed</span>}</td>}
        <td className="source">{item.source || '—'}</td>
      </tr>)}
    </tbody>
  </Table>;
};

const passLabel = (value: boolean | null | undefined) => value == null ? 'untested' : value ? 'passes' : 'fails';

const HIDDEN_ROW_KEYS = new Set(['id', 'tenant_id', 'facility_id', 'facility_version_id', 'created_at', 'updated_at', 'created_by',
  'carried_from_id', 'effective_from', 'effective_to', 'source_clause', 'source_page', 'source_section', 'enabled', 'status',
  'completion_json', 'authority', 'extracted_payload_json', 'rule_set_id', 'reviewed_by', 'reviewed_at', 'verified_by',
  'verified_at', 'activated_by', 'activated_at', 'source_reference', 'source_document_job_id', 'authority_status', 'state']);

const summariseRow = (row: Record<string, unknown>): string => Object.entries(row)
  .filter(([key, value]) => !HIDDEN_ROW_KEYS.has(key) && value !== null && value !== undefined && value !== '')
  .map(([key, value]) => `${fieldLabel(key)} ${formatValue(value, key)}`)
  .join(' · ');

/* ------------------------------------------------------------------ groups */

/** What the maker sends for one term: the governed decision the onboarding review also uses. */
export interface TermDecisionInput { state: 'VERIFIED' | 'NOT_STATED' | 'REJECTED'; verifiedValue?: unknown; sourceClause?: string | null }
export interface TermDecisionControls {
  /** The maker may decide (server-computed `actorMayPrepare`); the server still refuses what it refuses. */
  canDecide: boolean;
  busyKey: string | null;
  decide: (termKey: string, input: TermDecisionInput) => Promise<void>;
}

const Decide = styled.div`
  margin: 0 0 16px; border: 1px solid #7c6420; border-radius: 9px; background: #1d1a12; padding: 12px 14px;
  h3 { margin: 0 0 4px; font-size: 14px; color: ${INK.strong}; }
  > p { color: ${INK.muted}; font-size: 13px; margin: 0 0 8px; }
  .row { display: grid; grid-template-columns: minmax(10rem, 1fr) minmax(12rem, 1.2fr) minmax(12rem, 1.3fr) auto; gap: 10px; align-items: start; padding: 10px 0; border-top: 1px solid ${INK.line}; }
  .row:first-of-type { border-top: 0; }
  .value { font-variant-numeric: tabular-nums; color: ${INK.strong}; font-weight: 650; }
  .meta { color: ${INK.muted}; font-size: 12px; margin-top: 2px; }
  .source { color: ${INK.faint}; font-size: 12px; overflow-wrap: anywhere; }
  .actions { display: flex; flex-wrap: wrap; gap: 6px; justify-content: flex-end; }
  .correct { grid-column: 2 / -1; display: grid; grid-template-columns: minmax(8rem, 1fr) minmax(10rem, 1.4fr) auto auto; gap: 8px; align-items: center; }
  .correct input { ${fieldBase}; }
  .problem { grid-column: 1 / -1; color: #f8a5a5; font-size: 12px; }
  @media (max-width: 900px) { .row, .correct { grid-template-columns: 1fr; } .correct { grid-column: 1; } .actions { justify-content: flex-start; } }
`;

const MONEY_TERMS = new Set(['totalCommitment', 'drawnAmount', 'reserveAmount', 'initialDrawnAmount']);
const locatorOf = (item: PendingTermDecision) => [item.sourceSection, item.sourcePage ? `p. ${item.sourcePage}` : null, item.sourceClause]
  .filter(Boolean).join(' · ');
const placeholderValue = (value: unknown) => value === null || value === undefined || value === ''
  || (typeof value === 'string' && /^(not extracted|n\/a|unknown)$/i.test(value.trim()));

const PendingTermRow: React.FC<{ item: PendingTermDecision; controls: TermDecisionControls }> = ({ item, controls }) => {
  const [correcting, setCorrecting] = useState(false);
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const busy = controls.busyKey === item.termKey;
  const locator = locatorOf(item);
  const money = MONEY_TERMS.has(item.termKey);
  const run = async (input: TermDecisionInput) => {
    setProblem(null);
    try { await controls.decide(item.termKey, input); setCorrecting(false); setValue(''); setReason(''); }
    catch (failure) { setProblem(failure instanceof Error ? failure.message : String(failure)); }
  };
  const corrected = (): unknown => {
    const typed = value.trim();
    if (money) return parseAmountShorthand(typed);
    const numeric = Number(typed.replace(/,/g, ''));
    return typed !== '' && Number.isFinite(numeric) && typeof item.extractedValue === 'number' ? numeric : typed;
  };
  const noValue = placeholderValue(item.extractedValue);
  return <div className="row">
    <div><strong>{item.label}</strong>
      <div className="meta">{item.state.toLowerCase()}{item.requiredForActivation ? ' · required' : item.material ? ' · material' : ''}{item.confidence != null ? ` · ${Math.round(Number(item.confidence) * (Number(item.confidence) <= 1 ? 100 : 1))}% confidence` : ''}</div></div>
    <div><span className="value">{noValue ? 'No value read' : formatValue(item.extractedValue, item.termKey)}</span>
      {item.before !== undefined && item.before !== null && <div className="meta">active version: {formatValue(item.before, item.termKey)}</div>}</div>
    <div className="source">{locator || 'No locator in the document'}</div>
    <div className="actions">
      <Button $variant="primary" type="button" disabled={!controls.canDecide || busy || noValue || !locator}
        title={!controls.canDecide ? 'A maker confirms terms; a different checker approves the amendment.' : noValue ? 'Nothing was read to confirm; correct it or mark it not stated.' : !locator ? 'Confirming needs the clause, section or page it comes from; correct it with a source.' : undefined}
        onClick={() => void run({ state: 'VERIFIED' })}>{busy ? 'Saving…' : 'Confirm'}</Button>
      <Button type="button" disabled={!controls.canDecide || busy} onClick={() => setCorrecting(open => !open)}>{correcting ? 'Cancel' : 'Correct'}</Button>
      {!item.requiredForActivation && <Button $variant="quiet" type="button" disabled={!controls.canDecide || busy || !locator}
        title={!locator ? 'Marking a term not stated needs the clause, section or page that shows it is absent.' : undefined}
        onClick={() => void run({ state: 'NOT_STATED' })}>Not stated</Button>}
    </div>
    {correcting && <div className="correct">
      <input aria-label={`Corrected ${item.label}`} placeholder={money ? 'e.g. 50MM' : 'Value from the document'} value={value}
        onChange={event => setValue(event.target.value)} onBlur={event => { if (money) setValue(replaceShorthandOnBlur(event.target.value)); }} />
      <input aria-label={`Source for ${item.label}`} placeholder={locator ? `Source (kept: ${locator})` : 'Clause, section or page, e.g. Amendment No. 1 §1, p. 1'} value={reason}
        onChange={event => setReason(event.target.value)} />
      <Button $variant="primary" type="button" disabled={busy || value.trim() === '' || (!locator && !reason.trim())}
        onClick={() => void run({ state: 'VERIFIED', verifiedValue: corrected(), sourceClause: reason.trim() || null })}>Save correction</Button>
      <span className="meta">{money && value ? `= ${formatNumber(parseAmountShorthand(value))}` : ''}</span>
    </div>}
    {problem && <div className="problem" role="alert">{problem}</div>}
  </div>;
};

export const TermsPane: React.FC<{ group: AmendmentGroup; controls?: TermDecisionControls }> = ({ group, controls }) => {
  const items = (group.items || []) as TermItem[];
  const pending = group.pendingDecisions || [];
  return <>
    {pending.length > 0 && <Decide id="amendment-terms-to-confirm" aria-label="Terms to confirm">
      <h3>{pending.length} term{pending.length === 1 ? '' : 's'} to confirm against the document</h3>
      <p>Each value as it was read, with the clause it came from. Confirm it, correct it with its source, or mark it not stated. A different checker approves the amendment once every term is decided.</p>
      {pending.map(item => controls
        ? <PendingTermRow key={item.termKey} item={item} controls={controls} />
        : <div className="row" key={item.termKey}><div><strong>{item.label}</strong></div><div className="value">{formatValue(item.extractedValue, item.termKey)}</div><div className="source">{locatorOf(item)}</div><div /></div>)}
    </Decide>}
    <Note style={{ marginBottom: 12 }}><strong>{group.summary}</strong><p>{group.inheritedCount ?? 0} term{group.inheritedCount === 1 ? '' : 's'} inherited from the active version with their clauses.</p></Note>
    {items.length > 0 && <Table>
      <thead><tr><th>Term</th><th>Before → after</th><th>Reaches</th><th>Source</th></tr></thead>
      <tbody>{items.map(item => <tr key={item.termKey}>
        <td><strong>{item.label}</strong>{item.material && <div><Chip $colour="#edcc82">Material</Chip></div>}</td>
        <td><Pair before={item.before} after={item.after} field={item.termKey} /></td>
        <td className="muted">{(item.dependents || []).length === 0 ? '—' : (item.dependents || []).map((dependent, index) => {
          const type = String(dependent.dependent_type ?? dependent.type ?? '');
          const key = String(dependent.dependent_key ?? dependent.key ?? '');
          const impact = String(dependent.impact_type ?? dependent.impact ?? '');
          return <div key={index}>{fieldLabel(type.toLowerCase())} · {key.replace(/_/g, ' ').toLowerCase()} <span style={{ color: INK.faint }}>({impact.toLowerCase()})</span></div>;
        })}</td>
        <td className="source">{item.source || '—'}{item.verifiedBy && <div>by {item.verifiedBy}</div>}</td>
      </tr>)}</tbody>
    </Table>}
  </>;
};

export const EligibilityPane: React.FC<{ group: AmendmentGroup }> = ({ group }) => {
  const simulation = group.simulation;
  const items = (group.items || []) as Array<FieldChange | DiffItem>;
  return <>
    <Note style={{ marginBottom: 12 }}><strong>{group.summary}</strong>
      {group.simulationUnavailable && <p>The checker acknowledges the missing simulation at approval; the amendment still takes effect on its contractual date.</p>}
    </Note>
    {items.length > 0 && <Table>
      <thead><tr><th>Change</th><th>Before → after</th></tr></thead>
      <tbody>{items.map((item, index) => 'change' in item
        ? <tr key={index}><td><strong>{item.label}</strong> <Chip $colour={CHANGE_COLOUR[item.change]}>{CHANGE_LABEL[item.change]}</Chip></td>
          <td>{item.fieldChanges.length ? item.fieldChanges.map(change => <div key={change.field}><span style={{ color: INK.muted, fontSize: 12 }}>{fieldLabel(change.field)}: </span><Pair before={change.before} after={change.after} field={change.field} /></div>) : summariseRow(item.change === 'REMOVED' ? item.beforeRow : item.afterRow)}</td></tr>
        : <tr key={index}><td><strong>{item.field}</strong></td><td><Pair before={item.before} after={item.after} field={item.field} />{item.unit === '%' ? '' : ''}</td></tr>)}</tbody>
    </Table>}
    {simulation && <SimulationTable simulation={simulation} />}
    <WorkingPanel blocks={group.working} open />
  </>;
};

const SimulationTable: React.FC<{ simulation: AmendmentSimulation }> = ({ simulation }) => {
  const rows: Array<[string, keyof AmendmentSimulation['baselineMetrics']]> = [
    ['Eligible balance', 'eligibleBalance'], ['Gross borrowing base', 'grossBorrowingBase'],
    ['Concentration reduction', 'concentrationReduction'], ['Reserve', 'reserveAmount'], ['Borrowing base', 'computedBorrowingBase'],
  ];
  return <>
    <h3 style={{ margin: '14px 0 4px' }}>Borrowing-base simulation on tape {simulation.tapeIngestId}{simulation.tapeAsOfDate ? ` (${formatDate(simulation.tapeAsOfDate)})` : ''}</h3>
    <p style={{ color: INK.muted, fontSize: 13 }}>Baseline = active terms and rule set · Candidate = this amendment's terms and rule set. Read-only; released collateral and approved bases are untouched.</p>
    <Table>
      <thead><tr><th>Figure</th><th className="num">Baseline</th><th className="num">Candidate</th><th className="num">Delta</th></tr></thead>
      <tbody>
        {rows.map(([label, key]) => <tr key={key}>
          <td>{label}</td>
          <td className="num">{formatNumber(simulation.baselineMetrics[key])}</td>
          <td className="num">{formatNumber(simulation.candidateMetrics[key])}</td>
          <td className="num">{signed(Number(simulation.candidateMetrics[key]) - Number(simulation.baselineMetrics[key]))}</td>
        </tr>)}
        <tr><td>Eligible loans</td><td className="num">{simulation.baselineMetrics.eligibleCount}</td><td className="num">{simulation.candidateMetrics.eligibleCount}</td>
          <td className="num">{simulation.impact.eligibilityGainedCount} gained · {simulation.impact.eligibilityLostCount} lost</td></tr>
      </tbody>
    </Table>
    {simulation.affectedAssets.length > 0 && <>
      <div style={{ color: INK.muted, fontSize: 13, marginBottom: 4 }}>{Math.min(10, simulation.affectedAssets.length)} of {simulation.impact.affectedAssetCount} affected loans, largest first</div>
      <Table>
        <thead><tr><th>Loan</th><th className="num">Balance</th><th>Eligibility</th><th className="num">Advance rate</th><th>Why</th></tr></thead>
        <tbody>{simulation.affectedAssets.slice(0, 10).map(asset => <tr key={asset.assetId}>
          <td>{asset.assetIdentifier}</td><td className="num">{formatNumber(asset.balance)}</td>
          <td>{asset.baselineEligible ? 'Eligible' : 'Ineligible'} → <strong>{asset.candidateEligible ? 'Eligible' : 'Ineligible'}</strong></td>
          <td className="num">{formatValue(asset.baselineAdvanceRatePct, 'pct')} → {formatValue(asset.candidateAdvanceRatePct, 'pct')}</td>
          <td className="source">{(asset.candidateReasons || []).join('; ') || '—'}</td>
        </tr>)}</tbody>
      </Table>
    </>}
  </>;
};

export const CovenantsPane: React.FC<{ group: AmendmentGroup }> = ({ group }) => <>
  <Note style={{ marginBottom: 12 }}><strong>{group.summary}</strong><p>A threshold change shows the latest test value against both thresholds.</p></Note>
  <DiffTable items={(group.items || []) as DiffItem[]} emptyText="No covenants on either version." showRun />
</>;

export const WaterfallPane: React.FC<{ group: AmendmentGroup }> = ({ group }) => {
  const nodes = group.nodes || [];
  const modes = Array.from(new Set(nodes.map(node => node.key.split(':')[0])));
  return <>
    <Note style={{ marginBottom: 12 }}><strong>{group.summary}</strong>
      {group.firstAppliesTo && <p>First applies to the <strong>{group.firstAppliesTo.period_label}</strong> payment on {formatDate(group.firstAppliesTo.payment_date)}. Finalised waterfall runs are never re-run by an amendment.</p>}
    </Note>
    {modes.map(mode => <div key={mode}>
      <h3 style={{ margin: '12px 0 2px' }}>{mode === 'PRE_TRIGGER' ? 'Normal waterfall' : mode === 'POST_TRIGGER' ? 'Post-trigger waterfall' : 'Both waterfalls'}</h3>
      <DiffTable items={nodes.filter(node => node.key.startsWith(mode + ':'))} emptyText="No lines." />
    </div>)}
    {nodes.length === 0 && <p style={{ color: INK.muted }}>No priority of payments on either version.</p>}
    <h3 style={{ margin: '12px 0 2px' }}>Consequences</h3>
    <DiffTable items={group.consequences || []} emptyText="No consequences on either version." showArmed />
  </>;
};

const VERDICT_COLOUR: Record<PeriodVerdict['verdict'], string> = { KEPT: '#a8b6ca', REGENERATES: '#edcc82', CREATED: '#7cddbe' };
const VERDICT_LABEL: Record<PeriodVerdict['verdict'], string> = { KEPT: 'Kept', REGENERATES: 'Regenerates', CREATED: 'New' };

export const CalendarPane: React.FC<{ group: AmendmentGroup }> = ({ group }) => <>
  <Note style={{ marginBottom: 12 }}><strong>{group.summary}</strong>
    {group.calendarCarriedFromVersionNo != null && <p>Calendar carried from version {group.calendarCarriedFromVersionNo} and bound to this version on activation.</p>}
  </Note>
  {(group.calendarChanges || []).length > 0 && <Table>
    <thead><tr><th>Calendar field</th><th>Before → after</th></tr></thead>
    <tbody>{(group.calendarChanges || []).map(change => <tr key={change.field}><td>{fieldLabel(change.field)}</td><td><Pair before={change.before} after={change.after} field={change.field} /></td></tr>)}</tbody>
  </Table>}
  {(group.periods || []).length > 0 && <>
    <h3 style={{ margin: '12px 0 2px' }}>Collection periods</h3>
    <Table>
      <thead><tr><th>Period</th><th>Status</th><th>Verdict</th><th>Determination</th><th>Payment</th><th>Why</th></tr></thead>
      <tbody>{(group.periods || []).map(period => <tr key={period.periodLabel}>
        <td><strong>{period.periodLabel}</strong><div style={{ color: INK.faint, fontSize: 12 }}>{formatDate(period.periodStart)} – {formatDate(period.periodEnd)}</div></td>
        <td className="muted">{period.status ? period.status.replace(/_/g, ' ').toLowerCase() : 'new'}</td>
        <td><Chip $colour={VERDICT_COLOUR[period.verdict]}>{VERDICT_LABEL[period.verdict]}</Chip></td>
        <td>{period.verdict === 'KEPT' || !period.amendedDeterminationDate ? formatDate(period.determinationDate) : <Pair before={period.determinationDate} after={period.amendedDeterminationDate} field="date" />}</td>
        <td>{period.verdict === 'KEPT' || !period.amendedPaymentDate ? formatDate(period.paymentDate) : <Pair before={period.paymentDate} after={period.amendedPaymentDate} field="date" />}</td>
        <td className="source">{period.note || period.reason}</td>
      </tr>)}</tbody>
    </Table>
  </>}
  <h3 style={{ margin: '12px 0 2px' }}>Contract accounts</h3>
  <DiffTable items={group.accounts || []} emptyText="No contract accounts on either version." />
</>;

export const LenderClassesPane: React.FC<{ group: AmendmentGroup }> = ({ group }) => <>
  <Note style={{ marginBottom: 12 }}><strong>{group.summary}</strong>
    {group.commitmentMismatch && <p style={{ color: '#f8a5a5' }}>Class commitments sum to {formatNumber(group.classCommitmentSum)} against a facility commitment of {formatNumber(group.totalCommitment)}; they must agree before activation.</p>}
    {!group.commitmentMismatch && group.classCommitmentSum != null && (group.items || []).length > 0 && <p>Class commitments sum to {formatNumber(group.classCommitmentSum)}, matching the facility commitment.</p>}
  </Note>
  <DiffTable items={(group.items || []) as DiffItem[]} emptyText="No lender classes are stated on either version." />
</>;

export const RulesPane: React.FC<{ group: AmendmentGroup }> = ({ group }) => <>
  <Note style={{ marginBottom: 12 }}><strong>{group.summary}</strong>
    {group.adoptedFromRuleSetId != null && <p>The open Rule Workbench proposal (rule set {group.adoptedFromRuleSetId}) was adopted into this amendment; its proposer counts as a preparer for four-eyes.</p>}
  </Note>
  <DiffTable items={(group.items || []) as DiffItem[]} emptyText="No eligibility rules on either version." />
</>;

export const BookOfRecordPane: React.FC<{ group: AmendmentGroup }> = ({ group }) => {
  const items = (group.items || []) as BookItem[];
  const [showDays, setShowDays] = useState(false);
  return <>
    <Note style={{ marginBottom: 12 }}>
      <strong>{group.sentence}</strong>
      <p>{group.mode === 'FULL_PLATFORM' ? 'AxiomFlow is the book of record for this facility.' : group.mode === 'SIDECAR' ? 'The host system is the book of record; AxiomFlow records the contract and flags the change.' : 'The facility holds no AxiomFlow position yet.'}</p>
      {group.markerTrade && <p style={{ color: INK.muted, fontSize: 13 }}>Marker trade {group.markerTrade.sourceRef} · quantity {group.markerTrade.quantity} at {group.markerTrade.price} · dated {formatDate(group.markerTrade.tradeDate)}</p>}
      {(group.handoffKinds || []).length > 0 && <p style={{ color: INK.muted, fontSize: 13 }}>Hand-off to the host: {(group.handoffKinds || []).map(kind => kind.replace(/_/g, ' ').toLowerCase()).join(', ')}</p>}
    </Note>
    {items.length > 0 && <Table>
      <thead><tr><th>Funded term</th><th>Before</th><th>After</th><th>Consequence</th></tr></thead>
      <tbody>{items.map(item => <tr key={item.label}>
        <td><strong>{item.label}</strong></td>
        <td className="muted">{item.before}</td>
        <td>{item.after}</td>
        <td>
          {item.accrual && <div>{item.accrual}</div>}
          {item.undrawnBefore != null && <div>Undrawn {formatNumber(item.undrawnBefore)} → <strong>{formatNumber(item.undrawnAfter)}</strong></div>}
          {item.refusal && <div style={{ color: '#f8a5a5' }}>{item.refusal}</div>}
        </td>
      </tr>)}</tbody>
    </Table>}
    {group.trueUp && <Note style={{ marginBottom: 12, borderColor: '#edcc8266' }}>
      <strong>Accrual true-up: {signed(group.trueUp.amount)} over {group.trueUp.days} booked day{group.trueUp.days === 1 ? '' : 's'} ({formatDate(group.trueUp.from)} – {formatDate(group.trueUp.to)})</strong>
      <p>{group.trueUp.note}</p>
      <p><button type="button" onClick={() => setShowDays(value => !value)} style={{ background: 'none', border: 0, color: INK.accentSoft, cursor: 'pointer', padding: 0, font: 'inherit' }}>{showDays ? 'Hide' : 'Show'} the day-by-day rows</button></p>
      {showDays && <Table>
        <thead><tr><th>Day</th><th className="num">Drawn</th><th className="num">Rate booked</th><th className="num">Rate amended</th><th className="num">Booked</th><th className="num">Amended</th><th className="num">Delta</th></tr></thead>
        <tbody>{group.trueUp.dayRows.map(row => <tr key={row.date}>
          <td>{formatDate(row.date)}</td><td className="num">{formatNumber(row.drawn)}</td><td className="num">{formatNumber(row.rateBooked, 4)}%</td><td className="num">{formatNumber(row.rateAmended, 4)}%</td>
          <td className="num">{formatNumber(row.booked)}</td><td className="num">{formatNumber(row.amended)}</td><td className="num">{signed(row.delta)}</td>
        </tr>)}</tbody>
      </Table>}
    </Note>}
    <WorkingPanel blocks={group.working} open />
  </>;
};

/** Render the pane for a group. */
export const GroupPane: React.FC<{ group: AmendmentGroup; termControls?: TermDecisionControls }> = ({ group, termControls }) => {
  switch (group.key) {
    case 'TERMS': return <TermsPane group={group} controls={termControls} />;
    case 'ELIGIBILITY': return <EligibilityPane group={group} />;
    case 'COVENANTS': return <CovenantsPane group={group} />;
    case 'WATERFALL': return <WaterfallPane group={group} />;
    case 'CALENDAR': return <CalendarPane group={group} />;
    case 'LENDER_CLASSES': return <LenderClassesPane group={group} />;
    case 'RULES': return <RulesPane group={group} />;
    case 'BOOK_OF_RECORD': return <BookOfRecordPane group={group} />;
    default: return <Note><strong>{group.summary}</strong></Note>;
  }
};
